import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faHistory,
  faClockRotateLeft,
  faSpinner,
  faUser,
  faTag,
  faCalendarAlt,
  faChevronDown,
  faChevronUp,
} from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors.jsx';
import useAuditLogs from '../../hooks/useAuditLogs';
import { useTranslation } from 'react-i18next';

const formatTimestamp = (ts) => {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch (e) {
    return ts;
  }
};

const ACTION_COLORS = {
  created: { bg: 'rgba(34,197,94,0.12)', text: '#16a34a', border: 'rgba(34,197,94,0.3)' },
  updated: { bg: 'rgba(59,130,246,0.12)', text: '#2563eb', border: 'rgba(59,130,246,0.3)' },
  deleted: { bg: 'rgba(239,68,68,0.12)', text: '#dc2626', border: 'rgba(239,68,68,0.3)' },
  approved: { bg: 'rgba(34,197,94,0.12)', text: '#16a34a', border: 'rgba(34,197,94,0.3)' },
  rejected: { bg: 'rgba(239,68,68,0.12)', text: '#dc2626', border: 'rgba(239,68,68,0.3)' },
  cancelled: { bg: 'rgba(239,68,68,0.12)', text: '#dc2626', border: 'rgba(239,68,68,0.3)' },
  confirmed: { bg: 'rgba(59,130,246,0.12)', text: '#2563eb', border: 'rgba(59,130,246,0.3)' },
  status_changed: { bg: 'rgba(168,85,247,0.12)', text: '#7c3aed', border: 'rgba(168,85,247,0.3)' },
  default: { bg: 'rgba(107,114,128,0.12)', text: '#4b5563', border: 'rgba(107,114,128,0.3)' },
};

const getActionStyle = (action) => {
  const key = (action || '').toLowerCase();
  return ACTION_COLORS[key] || ACTION_COLORS.default;
};

// Turn a snake_case / camelCase key into a "Title Case" label.
const humanizeKey = (key) =>
  String(key)
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();

// Strip a Python-enum prefix ("FreightType.FREE_DELIVERY" -> "FREE_DELIVERY")
// and tidy the remaining token into readable words.
const cleanEnum = (s) => {
  const raw = String(s);
  const m = raw.match(/^[A-Za-z_][A-Za-z0-9_]*\.([A-Za-z0-9_]+)$/);
  const token = m ? m[1] : raw;
  // Only prettify all-caps enum tokens; leave normal text/values alone.
  if (/^[A-Z0-9_]+$/.test(token) && /[A-Z]/.test(token)) {
    return token.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return token;
};

// Render a single scalar value cleanly. Objects/arrays are formatted, never dumped raw.
const formatScalar = (value, t) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? t('auditLogs.yes') : t('auditLogs.no');
  if (Array.isArray(value)) {
    if (value.length === 0) return '—';
    // Array of scalars → comma list; array of objects handled by callers.
    if (value.every((v) => typeof v !== 'object' || v === null)) return value.map((v) => formatScalar(v, t)).join(', ');
    return t('auditLogs.itemsCount', { count: String(value.length), plural: value.length === 1 ? '' : 's' });
  }
  if (typeof value === 'object') {
    const label = value.name || value.product_name || value.alias_name || value.label;
    return label ? String(label) : '—';
  }
  return cleanEnum(value);
};

// Is this a {old, new} (or {old_value, new_value}) diff pair?
const asDiffPair = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  if ('old' in value || 'new' in value) return { old: value.old, next: value.new };
  if ('old_value' in value || 'new_value' in value) return { old: value.old_value, next: value.new_value };
  return null;
};

// Did the value actually change? Compare loosely so "13" (string) === 13 (number)
// and null/undefined/'' all read as "empty" — avoids showing no-op diffs.
const isEmpty = (v) => v === null || v === undefined || v === '';
const pairChanged = (pair) => {
  if (!pair) return false;
  const { old: o, next: n } = pair;
  if (isEmpty(o) && isEmpty(n)) return false;
  return String(o) !== String(n);
};

// old → new inline diff
const DiffValue = ({ oldVal, newVal }) => {
  const { t } = useTranslation();
  return (
  <span className="inline-flex items-center gap-1.5 flex-wrap">
    <span
      className="px-1.5 py-0.5 rounded line-through"
      style={{ backgroundColor: 'rgba(239,68,68,0.10)', color: '#b91c1c' }}
    >
      {formatScalar(oldVal, t)}
    </span>
    <span style={{ color: UI_COLORS.TEXT.MUTED }}>→</span>
    <span
      className="px-1.5 py-0.5 rounded"
      style={{ backgroundColor: 'rgba(34,197,94,0.12)', color: '#15803d' }}
    >
      {formatScalar(newVal, t)}
    </span>
  </span>
  );
};

const Row = ({ label, children }) => (
  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 py-0.5">
    <span className="font-semibold whitespace-nowrap" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
      {label}
    </span>
    <span style={{ color: UI_COLORS.TEXT.PRIMARY }}>{children}</span>
  </div>
);

// True if a {field: {old,new}} changes object has at least one real change.
const hasRealChanges = (changes) =>
  Object.values(changes || {}).some((val) => {
    const pair = asDiffPair(val);
    return pair ? pairChanged(pair) : !isEmpty(val);
  });

// Render a {field: {old,new}} changes object as a list of field diffs.
// Fields whose value did not actually change are skipped.
const ChangesList = ({ changes }) => {
  const { t } = useTranslation();
  const rows = Object.entries(changes || {})
    .map(([field, val]) => ({ field, val, pair: asDiffPair(val) }))
    .filter(({ val, pair }) => (pair ? pairChanged(pair) : !isEmpty(val)));

  if (rows.length === 0) {
    return <span style={{ color: UI_COLORS.TEXT.MUTED }}>{t('auditLogs.noFieldChanges')}</span>;
  }

  return (
    <div className="flex flex-col gap-0.5">
      {rows.map(({ field, val, pair }) => (
        <Row key={field} label={humanizeKey(field)}>
          {pair ? <DiffValue oldVal={pair.old} newVal={pair.next} /> : formatScalar(val, t)}
        </Row>
      ))}
    </div>
  );
};

const itemName = (it, t) =>
  it?.name || it?.product_name || it?.alias_name ||
  (it?.product_size_id ? t('auditLogs.productFallback', { id: it.product_size_id }) : t('auditLogs.itemFallback'));

// Render items_change: { added:[], removed:[], modified:[{name, changes}] }
const ItemsChange = ({ change }) => {
  const { t } = useTranslation();
  const { added = [], removed = [], modified = [] } = change || {};
  return (
    <div className="flex flex-col gap-2">
      {added.length > 0 && (
        <div>
          <div className="text-xs font-semibold mb-0.5" style={{ color: '#15803d' }}>{t('auditLogs.sections.added')}</div>
          {added.map((it, i) => (
            <div key={i} style={{ color: UI_COLORS.TEXT.PRIMARY }}>
              + {itemName(it, t)}{it.quantity != null ? ` — ${t('auditLogs.qtyLabel')} ${formatScalar(it.quantity, t)}` : ''}{it.price != null ? ` @ ${formatScalar(it.price, t)}` : ''}
            </div>
          ))}
        </div>
      )}
      {removed.length > 0 && (
        <div>
          <div className="text-xs font-semibold mb-0.5" style={{ color: '#b91c1c' }}>{t('auditLogs.sections.removed')}</div>
          {removed.map((it, i) => (
            <div key={i} className="line-through" style={{ color: UI_COLORS.TEXT.MUTED }}>
              − {itemName(it, t)}
            </div>
          ))}
        </div>
      )}
      {modified.length > 0 && (
        <div>
          <div className="text-xs font-semibold mb-0.5" style={{ color: '#2563eb' }}>{t('auditLogs.sections.modified')}</div>
          {modified.map((it, i) => (
            <div key={i} className="mb-1">
              <div className="font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{itemName(it, t)}</div>
              {it.changes && <div className="pl-3"><ChangesList changes={it.changes} /></div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Keys that carry no display value (internal ids / redundant with the message).
const HIDDEN_DETAIL_KEYS = new Set(['field']);

const DetailsSection = ({ details }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  if (!details || typeof details !== 'object' || Object.keys(details).length === 0) return null;

  // Special status/generic pair: { old_value, new_value } at the top level.
  const topPair = asDiffPair(details);
  const topPairChanged = pairChanged(topPair);

  const itemsChangeHasContent = (v) =>
    v && typeof v === 'object' && !Array.isArray(v) &&
    ((v.added && v.added.length) || (v.removed && v.removed.length) || (v.modified && v.modified.length));

  // Keep only entries that carry real information (drop no-op diffs and empty sections).
  const entries = Object.entries(details).filter(([k, v]) => {
    if (HIDDEN_DETAIL_KEYS.has(k)) return false;
    if (topPair && (k === 'old_value' || k === 'new_value')) return false; // shown via topPair
    if (k === 'changes') return v && typeof v === 'object' && hasRealChanges(v);
    if (k === 'items_change') return itemsChangeHasContent(v);
    if (k === 'items_actual_change') return Array.isArray(v) && v.length > 0;
    const pair = asDiffPair(v);
    if (pair) return pairChanged(pair); // drop "13 → 13"
    return true; // plain metadata (order_number, po_id, flags, …)
  });

  // Nothing meaningful to show (e.g. an update that only touched no-op fields).
  if (!topPairChanged && entries.length === 0) return null;

  const renderValue = (key, value) => {
    if (key === 'changes' && value && typeof value === 'object' && !Array.isArray(value)) {
      return <ChangesList changes={value} />;
    }
    if (key === 'items_change' && value && typeof value === 'object' && !Array.isArray(value)) {
      return <ItemsChange change={value} />;
    }
    if (key === 'items_actual_change' && Array.isArray(value)) {
      return (
        <div className="flex flex-col gap-1">
          {value.map((it, i) => (
            <div key={i}>
              <div className="font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{itemName(it, t)}</div>
              {it.changes && <div className="pl-3"><ChangesList changes={it.changes} /></div>}
            </div>
          ))}
        </div>
      );
    }
    const pair = asDiffPair(value);
    if (pair) return <DiffValue oldVal={pair.old} newVal={pair.next} />;
    return formatScalar(value, t);
  };

  return (
    <div className="mt-2">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center gap-1 text-xs font-medium transition-colors"
        style={{ color: UI_COLORS.TEXT.MUTED }}
      >
        <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} className="text-xs" />
        {expanded ? t('auditLogs.hideDetails') : t('auditLogs.showDetails')}
      </button>
      {expanded && (
        <div
          className="mt-2 rounded-lg p-3 text-xs flex flex-col gap-1"
          style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.BACKGROUNDS.SECTION || '#f3f4f6', 0.8), border: `1px solid ${UI_COLORS.BORDERS.LIGHT}` }}
        >
          {/* Top-level old→new pair (e.g. status change) */}
          {topPairChanged && (
            <Row label={humanizeKey(details.field || t('auditLogs.valueLabel'))}>
              <DiffValue oldVal={topPair.old} newVal={topPair.next} />
            </Row>
          )}
          {entries
            .map(([key, value]) => {
              // Section-style keys render label above, value below.
              const isBlock = key === 'changes' || key === 'items_change' || key === 'items_actual_change';
              if (isBlock) {
                return (
                  <div key={key}>
                    <div className="font-semibold mb-1" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{humanizeKey(key)}</div>
                    <div className="pl-1">{renderValue(key, value)}</div>
                  </div>
                );
              }
              return (
                <Row key={key} label={humanizeKey(key)}>
                  {renderValue(key, value)}
                </Row>
              );
            })}
        </div>
      )}
    </div>
  );
};

// The backend `message` for updates lists every field (including no-op diffs),
// which is exactly the noise we clean up in the details. When we have a
// structured `details` payload to render, show only the message's leading
// summary (the part before the first ":"); otherwise show the full message.
const summarizeMessage = (message, details) => {
  if (!message) return null;
  const hasStructuredDetails =
    details && typeof details === 'object' &&
    (details.changes || details.items_change || details.items_actual_change);
  if (!hasStructuredDetails) return message;
  const idx = message.indexOf(':');
  return idx > 0 ? message.slice(0, idx).trim() : message;
};

const LogEntry = ({ log, isTimeline = false }) => {
  const { t } = useTranslation();
  const style = getActionStyle(log.action);
  const summary = summarizeMessage(log.message, log.details);

  return (
    <div className="flex gap-3">
      {/* Timeline dot */}
      <div className="flex flex-col items-center">
        <div
          className="w-3 h-3 rounded-full mt-1 shrink-0 border-2"
          style={{ backgroundColor: style.text, borderColor: style.border }}
        />
        <div className="w-px flex-1 mt-1" style={{ backgroundColor: UI_COLORS.BORDERS.LIGHT }} />
      </div>

      {/* Content */}
      <div className="flex-1 pb-4">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          {/* Action badge */}
          <span
            className="px-2 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wide"
            style={{ backgroundColor: style.bg, color: style.text, border: `1px solid ${style.border}` }}
          >
            {(log.action || '').replace(/_/g, ' ')}
          </span>

          {/* Entity label for timeline */}
          {isTimeline && log.entity_label && (
            <span className="flex items-center gap-1 text-xs" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
              <FontAwesomeIcon icon={faTag} className="text-xs" />
              {log.entity_label}
            </span>
          )}

          {/* Entity type */}
          {log.entity_type && (
            <span className="text-xs font-medium" style={{ color: UI_COLORS.TEXT.MUTED }}>
              {log.entity_type.replace(/_/g, ' ')}
            </span>
          )}
        </div>

        {/* Human-readable summary (trimmed for structured updates) */}
        {summary && (
          <p className="text-sm mb-1" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
            {summary}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-3 text-xs" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
          <span className="flex items-center gap-1">
            <FontAwesomeIcon icon={faUser} className="text-xs" />
            {log.performed_by_name || (log.performed_by != null ? t('auditLogs.userFallback', { id: log.performed_by }) : '—')}
          </span>
          <span className="flex items-center gap-1">
            <FontAwesomeIcon icon={faCalendarAlt} className="text-xs" />
            {formatTimestamp(log.timestamp)}
          </span>
        </div>

        <DetailsSection details={log.details} />
      </div>
    </div>
  );
};

const TABS = {
  AUDIT: 'audit',
  TIMELINE: 'timeline',
};

/**
 * AuditLogsModal
 *
 * Props:
 *   isOpen        - boolean
 *   onClose       - fn
 *   entityType    - 'order' | 'purchase_order' | 'delivery_sub_order' | 'purchase_advice' | 'inquiry'
 *   entityId      - number
 *   entityLabel   - string (shown in header, e.g. order number)
 *   hasTimeline   - boolean (show timeline tab for orders and purchase orders)
 */
const AuditLogsModal = ({ isOpen, onClose, entityType, entityId, entityLabel, hasTimeline = false }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(TABS.AUDIT);
  const {
    logs,
    timeline,
    loading,
    error,
    fetchOrderAuditLogs,
    fetchPurchaseOrderAuditLogs,
    fetchDeliverySubOrderAuditLogs,
    fetchPurchaseAdviceAuditLogs,
    fetchInquiryAuditLogs,
    fetchOrderTimeline,
    fetchPurchaseOrderTimeline,
    reset,
  } = useAuditLogs();

  const loadData = useCallback(async (tab) => {
    if (!entityId) return;

    if (tab === TABS.AUDIT) {
      if (entityType === 'order') await fetchOrderAuditLogs(entityId);
      else if (entityType === 'purchase_order') await fetchPurchaseOrderAuditLogs(entityId);
      else if (entityType === 'delivery_sub_order') await fetchDeliverySubOrderAuditLogs(entityId);
      else if (entityType === 'purchase_advice') await fetchPurchaseAdviceAuditLogs(entityId);
      else if (entityType === 'inquiry') await fetchInquiryAuditLogs(entityId);
    } else if (tab === TABS.TIMELINE) {
      if (entityType === 'order') await fetchOrderTimeline(entityId);
      else if (entityType === 'purchase_order') await fetchPurchaseOrderTimeline(entityId);
    }
  }, [entityId, entityType, fetchOrderAuditLogs, fetchPurchaseOrderAuditLogs, fetchDeliverySubOrderAuditLogs, fetchPurchaseAdviceAuditLogs, fetchInquiryAuditLogs, fetchOrderTimeline, fetchPurchaseOrderTimeline]);

  useEffect(() => {
    if (isOpen && entityId) {
      setActiveTab(TABS.AUDIT);
      reset();
      loadData(TABS.AUDIT);
    }
  }, [isOpen, entityId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    loadData(tab);
  };

  if (!isOpen) return null;

  const displayData = activeTab === TABS.AUDIT ? logs : timeline;
  const entityTypeLabel = {
    order: t('auditLogs.entityLabels.order'),
    purchase_order: t('auditLogs.entityLabels.purchase_order'),
    delivery_sub_order: t('auditLogs.entityLabels.delivery_sub_order'),
    purchase_advice: t('auditLogs.entityLabels.purchase_advice'),
    inquiry: t('auditLogs.entityLabels.inquiry'),
  }[entityType] || entityType;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div
        className="w-full max-w-2xl max-h-[85vh] flex flex-col rounded-2xl shadow-2xl overflow-hidden"
        style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 border-b shrink-0"
          style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
        >
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
              {entityTypeLabel}
            </p>
            <h2 className="text-xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
              {t('auditLogs.heading')}{entityLabel ? ` — ${entityLabel}` : ''}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
            style={{ color: UI_COLORS.TEXT.MUTED, backgroundColor: colorHelpers.withOpacity(UI_COLORS.BORDERS.LIGHT, 0.5) }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Tabs (only when timeline available) */}
        {hasTimeline && (
          <div className="flex border-b shrink-0" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
            <button
              onClick={() => handleTabChange(TABS.AUDIT)}
              className="flex items-center gap-2 px-5 py-3 text-sm font-medium transition-colors border-b-2"
              style={{
                color: activeTab === TABS.AUDIT ? UI_COLORS.TEXT.PRIMARY : UI_COLORS.TEXT.MUTED,
                borderBottomColor: activeTab === TABS.AUDIT ? UI_COLORS.TEXT.PRIMARY : 'transparent',
              }}
            >
              <FontAwesomeIcon icon={faHistory} className="text-xs" />
              {t('auditLogs.tabs.auditLog')}
            </button>
            <button
              onClick={() => handleTabChange(TABS.TIMELINE)}
              className="flex items-center gap-2 px-5 py-3 text-sm font-medium transition-colors border-b-2"
              style={{
                color: activeTab === TABS.TIMELINE ? UI_COLORS.TEXT.PRIMARY : UI_COLORS.TEXT.MUTED,
                borderBottomColor: activeTab === TABS.TIMELINE ? UI_COLORS.TEXT.PRIMARY : 'transparent',
              }}
            >
              <FontAwesomeIcon icon={faClockRotateLeft} className="text-xs" />
              {t('auditLogs.tabs.timeline')}
            </button>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <FontAwesomeIcon icon={faSpinner} spin className="text-2xl" style={{ color: UI_COLORS.TEXT.MUTED }} />
            </div>
          )}

          {!loading && error && (
            <div
              className="rounded-xl px-4 py-3 text-sm"
              style={{ backgroundColor: 'rgba(239,68,68,0.1)', color: '#dc2626', border: '1px solid rgba(239,68,68,0.3)' }}
            >
              {t('auditLogs.failedToLoad', { error })}
            </div>
          )}

          {!loading && !error && displayData.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 gap-2">
              <FontAwesomeIcon icon={faHistory} className="text-3xl" style={{ color: UI_COLORS.TEXT.MUTED }} />
              <p className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('auditLogs.noLogsFound')}</p>
            </div>
          )}

          {!loading && !error && displayData.length > 0 && (
            <div className="pt-2">
              {displayData.map((log, idx) => (
                <LogEntry key={log.id ?? idx} log={log} isTimeline={activeTab === TABS.TIMELINE} />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="px-6 py-3 border-t shrink-0 flex items-center justify-between"
          style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
        >
          <span className="text-xs" style={{ color: UI_COLORS.TEXT.MUTED }}>
            {!loading && `${displayData.length} ${displayData.length === 1 ? t('auditLogs.entry') : t('auditLogs.entries')}`}
          </span>
          <button
            onClick={onClose}
            className="px-4 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 hover:scale-105"
            style={{ color: UI_COLORS.TEXT.PRIMARY, backgroundColor: colorHelpers.withOpacity(UI_COLORS.BORDERS.LIGHT, 0.6) }}
          >
            {t('auditLogs.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuditLogsModal;
